import React, { useEffect, useMemo, useRef, useState } from 'react';
import FeedSignBadge from './FeedSignBadge';
import FeedCommentComposer from './FeedCommentComposer';
import { formatRelativeDate } from '../utils/formatDate';

import { useToast } from '../context/ToastProvider';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const feedText = (es, en) => localizedValue({ es, en });




function resolveAvatarUrl(author = {}) {
  const raw =
    author?.avatarUrl ||
    author?.photoPath ||
    author?.profilePicture ||
    author?.profilePhoto ||
    author?.profileImage ||
    author?.avatar ||
    author?.image ||
    author?.photo ||
    author?.photoURL ||
    '';

  const value = String(raw || '').trim();
  if (!value) return '';

  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }

  return `${REST_API_ORIGIN}/${value.replace(/^\//, '')}`;
}

function getAuthorInitials(author = {}) {
  const fullName = String(
    author?.fullName || author?.name || author?.username || 'U'
  ).trim();

  if (!fullName) return 'U';

  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase() || 'U';
}

function normalizeComments(items = []) {
  return (Array.isArray(items) ? items : []).map((comment) => ({
    ...comment,
    id: String(
      comment?.id ||
      comment?._id ||
      comment?.commentId ||
      ''
    ).trim(),
    stats: {
      ...(comment?.stats || {}),
      likeCount: Number(comment?.stats?.likeCount || comment?.likeCount || 0),
    },
    viewer: {
      ...(comment?.viewer || {}),
      liked: !!(comment?.viewer?.liked || comment?.likedByMe),
      isOwner: !!comment?.viewer?.isOwner,
    },
  }));
}

export default function FeedCommentList({
  post = null,
  comments = [],
  loading = false,
  creating = false,
  error = '',
  onSubmitComment,
  onDeleteComment,
  onLikeComment,
  onReportComment,
  className = '',
}) {
  const { showToast } = useToast();

  const [composerOpen, setComposerOpen] = useState(false);

  // Remove local state for commentItems; use memoized value instead.
  // Memoized normalized comments based on props
  const commentItems = useMemo(() => normalizeComments(comments), [comments]);
 const [pendingLikeIds, setPendingLikeIds] = useState({});
const [pendingDeleteIds, setPendingDeleteIds] = useState({});
const [replyContext, setReplyContext] = useState(null);
const [likeOverrides, setLikeOverrides] = useState({});

  const [openMenuCommentId, setOpenMenuCommentId] = useState('');
  const [reportingIds, setReportingIds] = useState({});
  const menuRefs = useRef({});
  const composerWrapRef = useRef(null);
  const composerTriggerRef = useRef(null);

  const handleSubmitAndClose = async (payload) => {
    if (typeof onSubmitComment !== 'function') return;

    const nextPayload = replyContext
      ? {
          ...payload,
          replyToCommentId: String(replyContext?.id || ''),
          replyToAuthorId: String(replyContext?.author?._id || replyContext?.author?.id || replyContext?.author?.userId || ''),
          replyToAuthorName: String(replyContext?.author?.fullName || replyContext?.author?.name || replyContext?.author?.username || 'Usuario'),
          replyPreviewText: String(replyContext?.text || '').trim(),
        }
      : payload;

    await onSubmitComment(nextPayload);
    setComposerOpen(false);
    setReplyContext(null);
    window.setTimeout(() => {
      try {
        composerTriggerRef.current?.focus?.();
      } catch {}
    }, 0);
  };

  const openReplyComposer = (comment = null) => {
    composerTriggerRef.current = document.activeElement;
    setOpenMenuCommentId('');
    setReplyContext(comment || null);
    setComposerOpen(true);

    if (typeof window === 'undefined') return;

    window.setTimeout(() => {
      const wrapNode = composerWrapRef.current;
      if (!wrapNode) return;

      try {
        wrapNode.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      } catch {}
    }, 120);
  };

  const handleCloseReplies = () => {
    setComposerOpen(false);
    setReplyContext(null);
    setOpenMenuCommentId('');
    window.setTimeout(() => {
      try {
        composerTriggerRef.current?.focus?.();
      } catch {}
    }, 0);
  };


  useEffect(() => {
    if (!replyContext) return;
    const replyId = String(replyContext?.id || '');
    const stillExists = commentItems.some((item) => String(item?.id || '') === replyId);
    if (!stillExists) {
      setReplyContext(null);
    }
  }, [commentItems, replyContext]);

useEffect(() => {
  setLikeOverrides({});
}, [comments]);

  useEffect(() => {
    if (loading) return;
    if (replyContext) return;
    if (composerOpen) return;
    if (commentItems.length > 0) return;

    setComposerOpen(true);
  }, [loading, replyContext, composerOpen, commentItems.length]);

  useEffect(() => {
    if (!composerOpen) return undefined;
    if (typeof window === 'undefined') return undefined;

    const timer = window.setTimeout(() => {
      const wrapNode = composerWrapRef.current;
      if (!wrapNode) return;

      try {
        wrapNode.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      } catch {}
    }, 140);

    return () => window.clearTimeout(timer);
  }, [composerOpen]);

  useEffect(() => {
    if (!openMenuCommentId) return undefined;

    const handlePointerDown = (event) => {
      const activeNode = menuRefs.current?.[openMenuCommentId];
      if (!activeNode) {
        setOpenMenuCommentId('');
        return;
      }

      if (activeNode.contains(event.target)) {
        return;
      }

      setOpenMenuCommentId('');
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenMenuCommentId('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuCommentId]);

  const handleLikeReply = async (comment) => {
  const commentId = String(comment?.id || comment?._id || comment?.commentId || '');
  if (!commentId) return;

  const baseLiked = Object.prototype.hasOwnProperty.call(likeOverrides, commentId)
    ? !!likeOverrides[commentId]?.liked
    : !!comment?.viewer?.liked;

  const baseLikeCount = Object.prototype.hasOwnProperty.call(likeOverrides, commentId)
    ? Number(likeOverrides[commentId]?.likeCount || 0)
    : Number(comment?.stats?.likeCount || comment?.likeCount || 0);

  const optimisticLiked = !baseLiked;
  const optimisticLikeCount = optimisticLiked
    ? baseLikeCount + 1
    : Math.max(0, baseLikeCount - 1);

  setPendingLikeIds((prev) => ({ ...prev, [commentId]: true }));
  setLikeOverrides((prev) => ({
    ...prev,
    [commentId]: {
      liked: optimisticLiked,
      likeCount: optimisticLikeCount,
    },
  }));

  try {
    if (typeof onLikeComment === 'function') {
      await onLikeComment({ ...comment, id: commentId }, commentId);
    }
  } catch (err) {
    setLikeOverrides((prev) => ({
      ...prev,
      [commentId]: {
        liked: baseLiked,
        likeCount: baseLikeCount,
      },
    }));
    showToast(err?.message || feedText('No se pudo actualizar el me gusta.', 'The like could not be updated.'), { type: 'error' });
  } finally {
    setPendingLikeIds((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  }
};

  const handleDeleteReply = async (comment) => {
    const commentId = String(comment?.id || '');
    if (!commentId || typeof onDeleteComment !== 'function') return;

    setPendingDeleteIds((prev) => ({ ...prev, [commentId]: true }));

    try {
      await onDeleteComment(comment);
      showToast('Respuesta eliminada', { type: 'success', duration: 1600 });
    } catch (err) {
      showToast(err?.message || feedText('No se pudo eliminar la respuesta.', 'The reply could not be deleted.'), { type: 'error' });
    } finally {
      setPendingDeleteIds((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
    }
  };


  const handleReportReply = async (comment) => {
    const commentId = String(comment?.id || '');
    if (!commentId) return;

    if (typeof onReportComment !== 'function') {
      showToast(feedText('El reporte de respuestas se conecta en el próximo paso.', 'Reply reporting will be available in the next step.'), { type: 'info', duration: 2000 });
      setOpenMenuCommentId('');
      return;
    }

    try {
      setReportingIds((prev) => ({ ...prev, [commentId]: true }));
      await onReportComment({
        ...comment,
        reportTargetType: 'comment',
        targetType: 'comment',
        entityType: 'comment',
        commentId,
        targetId: commentId,
        parentPostId: String(comment?.postId || comment?.post || comment?.post?._id || comment?.post?.id || ''),
      });
      showToast('Respuesta reportada', { type: 'success', duration: 1600 });
    } catch (err) {
      showToast(err?.message || feedText('No se pudo reportar la respuesta.', 'The reply could not be reported.'), { type: 'error' });
    } finally {
      setReportingIds((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
      setOpenMenuCommentId('');
    }
  };

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        overflow: 'visible',
        overflowX: 'clip',
        width: '100%',
        maxWidth: '100%',
        zIndex: 1,
      }}
    >
            <div style={{ display: 'flex', alignItems: 'center' }}>
        <h3 id="feed-comment-list-heading">Respuestas</h3>
        {composerOpen ? (
  <button
    type="button"
    onClick={handleCloseReplies}
    style={{
      marginLeft: 8,
      padding: '4px 10px',
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.06)',
      background: 'rgba(255,255,255,0.03)',
      color: 'rgba(255,255,255,0.68)',
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      opacity: 0.88,
      transition: 'all 0.18s ease',
      boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
    }}
  >
    {feedText('Cerrar respuestas', 'Hide replies')}
  </button>
) : null}
      </div>

      {loading && <p>{feedText('Cargando comentarios...', 'Loading comments...')}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}


      <div
        role="list"
        aria-live="polite"
        aria-label="Lista de respuestas"
        aria-labelledby="feed-comment-list-heading"
        aria-atomic="false"
      >
      {commentItems.map((comment) => {
        const commentId = String(comment?.id || '');
        const commentAuthorId = String(comment?.author?._id || comment?.author?.id || comment?.author?.userId || '').trim();
        const resolvedAvatarUrl = resolveAvatarUrl(comment.author);
        const authorInitials = getAuthorInitials(comment.author);
        const likeOverride = likeOverrides[commentId] || null;
const likeCount = Number(likeOverride?.likeCount ?? comment?.stats?.likeCount ?? comment?.likeCount ?? 0);
const isLiked = typeof likeOverride?.liked === 'boolean' ? !!likeOverride.liked : !!comment?.viewer?.liked;
        const isOwner = !!comment?.viewer?.isOwner;
        const viewerOwnsPost = !!post?.viewer?.isOwner;
        const canDeleteReply = isOwner || viewerOwnsPost;
        const isReplyTarget = String(replyContext?.id || '') === commentId;

        const handleOpenCommentAuthorProfile = (event) => {
          event?.stopPropagation?.();
          if (!commentAuthorId || typeof window === 'undefined') return;
          window.location.assign(`/profile/${commentAuthorId}`);
        };

        return (
        <div
          key={comment.id}
          role="listitem"
          style={{
            position: 'relative',
            zIndex: openMenuCommentId === commentId ? 40 : 1,
            overflow: 'visible',
            marginBottom: 16,
            padding: '12px 12px 10px',
            borderRadius: 18,
            border: isReplyTarget ? '1px solid rgba(255,224,130,0.22)' : '1px solid rgba(255,255,255,0.07)',
            background: isReplyTarget
              ? 'linear-gradient(180deg, rgba(255,224,130,0.08), rgba(255,255,255,0.025))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
            boxShadow: isReplyTarget
              ? '0 12px 28px rgba(0,0,0,0.16), 0 0 20px rgba(255,224,130,0.08)'
              : 'none',
            transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <button
                type="button"
                onClick={handleOpenCommentAuthorProfile}
                aria-label={feedText(`Abrir perfil de ${comment.author?.fullName || comment.author?.name || comment.author?.username || 'usuario'}`, `Open ${comment.author?.fullName || comment.author?.name || comment.author?.username || 'user'}'s profile`)}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  marginRight: 8,
                  cursor: commentAuthorId ? 'pointer' : 'default',
                  borderRadius: '50%',
                  flex: '0 0 auto',
                }}
              >
                {resolvedAvatarUrl ? (
                  <img
                    src={resolvedAvatarUrl}
                    alt={comment.author?.fullName || comment.author?.name || 'Avatar'}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      display: 'block',
                      border: '2px solid rgba(255,224,130,0.42)',
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.10) inset, 0 0 12px rgba(255,224,130,0.10)',
                    }}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(255,224,130,0.22), rgba(183,140,255,0.24))',
                      color: '#FFF6D6',
                      fontSize: 11,
                      fontWeight: 900,
                      letterSpacing: 0.35,
                      border: '2px solid rgba(255,224,130,0.42)',
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.10) inset, 0 0 12px rgba(255,224,130,0.10)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {authorInitials}
                  </span>
                )}
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleOpenCommentAuthorProfile}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      color: '#FFF7DA',
                      fontWeight: 700,
                      cursor: commentAuthorId ? 'pointer' : 'default',
                    }}
                  >
                    {comment.author?.fullName || comment.author?.name || comment.author?.username || 'Anon'}
                  </button>
                  <FeedSignBadge sign={comment.author?.zodiacSign || comment.author?.sign} />
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)' }}>
                  {formatRelativeDate(comment.createdAt)}
                </div>
              </div>
            </div>

            <div
              ref={(node) => {
                if (node) {
                  menuRefs.current[commentId] = node;
                } else {
                  delete menuRefs.current[commentId];
                }
              }}
              style={{ position: 'relative', flex: '0 0 auto', zIndex: openMenuCommentId === commentId ? 50 : 1 }}
            >
              <button
                type="button"
                onClick={() => setOpenMenuCommentId((prev) => (prev === commentId ? '' : commentId))}
                style={{
                  border: '1px solid rgba(255,255,255,0.10)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#F7F2FF',
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
                aria-label={feedText('Abrir acciones de la respuesta', 'Open reply actions')}
              >
                ⋯
              </button>

              {openMenuCommentId === commentId ? (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    top: 'auto',
                    right: -2,
                    minWidth: 220,
                    maxWidth: 'min(88vw, 280px)',
                    transformOrigin: 'bottom right',
                    marginBottom: 2,
                    borderRadius: 16,
                    border: '1px solid rgba(255,255,255,0.07)',
                    background: 'linear-gradient(180deg, rgba(25,9,42,0.985), rgba(18,5,31,0.985))',
                    boxShadow: '0 20px 34px rgba(0,0,0,0.24), 0 0 0 1px rgba(255,255,255,0.025) inset, 0 0 22px rgba(255,215,0,0.05)',
                    padding: 8,
                    zIndex: 5000,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    overflow: 'hidden',
                  }}
                >

                  <button
  type="button"
  onClick={() => handleReportReply(comment)}
  disabled={!!reportingIds[commentId]}
  style={{
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 13,
    padding: '10px 11px',
    cursor: reportingIds[commentId] ? 'default' : 'pointer',
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: '#F6F0FF',
    fontWeight: 900,
    fontSize: 12,
    opacity: reportingIds[commentId] ? 0.72 : 1,
  }}
>
  <span aria-hidden="true">⚑</span>
  {reportingIds[commentId] ? 'Reportando...' : 'Reportar'}
</button>

                  {canDeleteReply ? (
                    <button
                      type="button"
                      onClick={() => handleDeleteReply(comment)}
                      disabled={!!pendingDeleteIds[commentId]}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        borderRadius: 13,
                        padding: '10px 11px',
                        cursor: pendingDeleteIds[commentId] ? 'default' : 'pointer',
                        background: 'linear-gradient(180deg, rgba(255,88,88,0.08), rgba(255,255,255,0.02))',
                        border: '1px solid rgba(255,88,88,0.12)',
                        color: '#FFD7D7',
                        fontWeight: 900,
                        fontSize: 12,
                        opacity: pendingDeleteIds[commentId] ? 0.72 : 1,
                      }}
                    >
                      <span aria-hidden="true">🗑</span>
                      {pendingDeleteIds[commentId] ? feedText('Eliminando...', 'Deleting...') : feedText('Eliminar respuesta', 'Delete reply')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <p style={{ margin: '10px 0 0', color: '#FFFDFC', fontSize: 14.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {comment.text}
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleLikeReply(comment)}
              disabled={!!pendingLikeIds[commentId]}
              style={{
                border: '1px solid rgba(255,255,255,0.08)',
                background: isLiked ? 'rgba(255,224,130,0.10)' : 'rgba(255,255,255,0.04)',
                color: isLiked ? '#FFE082' : '#FFD700',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 12,
                borderRadius: 999,
                padding: '6px 10px',
                opacity: pendingLikeIds[commentId] ? 0.72 : 1,
              }}
            >
              {isLiked ? '♥' : '♡'} {likeCount}
            </button>

            <button
              type="button"
              onClick={() => openReplyComposer(comment)}
              style={{
                border: isReplyTarget ? '1px solid rgba(255,224,130,0.20)' : '1px solid rgba(255,255,255,0.08)',
                background: isReplyTarget ? 'rgba(255,224,130,0.08)' : 'rgba(255,255,255,0.04)',
                color: isReplyTarget ? '#FFE8A3' : '#FFF',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 12,
                borderRadius: 999,
                padding: '6px 10px',
                boxShadow: isReplyTarget ? '0 0 14px rgba(255,224,130,0.08)' : 'none',
              }}
            >
              Responder
            </button>
          </div>
        </div>
        );
      })}
      </div>

      {composerOpen && (
        <div
          ref={composerWrapRef}
          style={{
            position: 'relative',
            zIndex: 1,
            scrollMarginTop: 120,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'clip',
          }}
        >
          {replyContext ? (
            <div
              style={{
                marginBottom: 8,
                padding: '9px 11px',
                borderRadius: 14,
                border: '1px solid rgba(255,224,130,0.14)',
                background: 'linear-gradient(180deg, rgba(255,224,130,0.07), rgba(255,255,255,0.028))',
                boxShadow: '0 8px 18px rgba(0,0,0,0.12), 0 0 14px rgba(255,224,130,0.05)',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, minWidth: 0, width: '100%' }}>
                <div style={{ minWidth: 0, flex: 1, maxWidth: '100%' }}>
                  <div style={{ color: '#FFE082', fontSize: 11, fontWeight: 900, letterSpacing: 0.12, textTransform: 'uppercase' }}>
                    Respondiendo a {replyContext?.author?.fullName || replyContext?.author?.name || replyContext?.author?.username || 'Usuario'}
                  </div>
                  <div
                    style={{
                      marginTop: 5,
                      color: 'rgba(255,248,222,0.88)',
                      fontSize: 12,
                      lineHeight: 1.4,
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '100%',
                      paddingLeft: 9,
                      borderLeft: '2px solid rgba(255,224,130,0.18)',
                    }}
                  >
                    {String(replyContext?.text || '').trim() || 'Sin texto visible'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setReplyContext(null);
                    window.setTimeout(() => {
                      try {
                        composerTriggerRef.current?.focus?.();
                      } catch {}
                    }, 0);
                  }}
                  style={{
                    border: '1px solid rgba(255,255,255,0.05)',
                    background: 'rgba(255,255,255,0.025)',
                    color: 'rgba(255,243,196,0.78)',
                    borderRadius: 999,
                    padding: '4px 8px',
                    fontSize: 10.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    opacity: 0.88,
                  }}
                >
                  {feedText('Cancelar', 'Cancel')}
                </button>
              </div>
            </div>
          ) : null}
          <FeedCommentComposer
  onSubmit={handleSubmitAndClose}
  submitting={creating}
  placeholder={replyContext ? feedText('Escribí tu respuesta...', 'Write your reply...') : undefined}
/>
        </div>
      )}
    </div>
  );
}
